import React, { useState, useEffect, useRef } from 'react';
import { HomePage } from './HomePage';
import { TranslatingPage } from '@/components/pages/TranslatingPage';
import { ChatsPage } from '@/components/pages/ChatsPage';
import { useWebRTCSession } from '@/contexts/WebRTCSessionContext';
import { useMicrophoneState } from '@/contexts/MicrophoneContext';
import { SessionConnectionStatus } from '@/services/interfaces/Session';
import { toast } from "sonner";

export type PageType = 'home' | 'translating';

interface PageTransitionProps {
  className?: string;
  currentPage?: PageType;
  onPageChange?: (page: PageType) => void;
}

export const PageTransition: React.FC<PageTransitionProps> = ({ 
  className = "",
  currentPage: externalCurrentPage,
  onPageChange
}) => {
  {/* TODO: 暂时先使用 PageTransition 组件，后续再使用 TranslationPanel 组件 */}
  // const [internalCurrentPage, setInternalCurrentPage] = useState<PageType>(import.meta.env.DEV ? 'translating' : 'home');
  const [internalCurrentPage, setInternalCurrentPage] = useState<PageType>('home');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionRef = useRef<HTMLDivElement>(null);

  // 使用外部传入的 currentPage，如果没有则使用内部状态
  const currentPage = externalCurrentPage ?? internalCurrentPage;
  const setCurrentPage = onPageChange ?? setInternalCurrentPage;

  // Use WebRTC session
  const {
    isSessionActive,
    sessionConnectionStatus,
    isSessionServiceInitialized,
    startTranslation,
    stopTranslation,
    error,
    clearError
  } = useWebRTCSession();

  // Use microphone state
  const { 
    hasPermission,
    selectedDeviceId
  } = useMicrophoneState();

  // 🔑 修复：一次性错误消费，使用ref跟踪已显示的错误
  const lastShownErrorRef = useRef<Error | null>(null);

  useEffect(() => {
    if (error && error !== lastShownErrorRef.current) {
      // 显示错误toast
      toast.error(`${error.message}`);
      // 停止翻译
      handleStopTranslation();
      // 记录已显示的错误
      lastShownErrorRef.current = error;
      // 立即清除错误状态，实现一次性消费
      clearError();
    }
  }, [error, clearError]);

  // 监听会话状态变化，自动切换页面
  useEffect(() => {
    {/* TODO: 暂时先使用 PageTransition 组件，后续再使用 TranslationPanel 组件 */}
    if (isSessionActive && currentPage === 'home') {
      // 会话激活时，从首页切换到翻译页
      handlePageTransition('translating');
    } else if (!isSessionActive && currentPage === 'translating' && sessionConnectionStatus !== SessionConnectionStatus.Connecting) {
      // 会话结束时，从翻译页切换回首页
      handlePageTransition('home');
    }
  }, [isSessionActive, currentPage, sessionConnectionStatus]);

  // 处理页面过渡动画
  const handlePageTransition = async (targetPage: PageType) => {
    if (isTransitioning || currentPage === targetPage) return;

    setIsTransitioning(true);

    // 执行神奇移动动画
    await performMagicMoveAnimation(currentPage, targetPage);

    // 切换页面
    setCurrentPage(targetPage);
    setIsTransitioning(false);
  };

  // 神奇移动动画实现
  const performMagicMoveAnimation = async (fromPage: PageType, toPage: PageType): Promise<void> => {
    return new Promise((resolve) => {
      const container = transitionRef.current;
      if (!container) {
        resolve();
        return;
      }

      // 获取语言选择器元素
      const sourceSelector = container.querySelector('#language-selector-container') as HTMLElement;
      // 获取所有需要fade效果的元素
      const fadeElements = container.querySelectorAll('[data-fade]') as NodeListOf<HTMLElement>;
      
      // 通用fade-out函数
      const fadeOut = (elements: NodeListOf<HTMLElement>) => {
        elements.forEach(el => {
          el.style.transition = 'opacity 0.3s ease-out';
          el.style.opacity = '0';
        });
      };

      // 通用fade-in函数
      const fadeIn = (elements: NodeListOf<HTMLElement>) => {
        elements.forEach(el => {
          el.style.transition = 'opacity 0.3s ease-in';
          el.style.opacity = '1';
        });
      };
      
      if (sourceSelector && fromPage === 'home' && toPage === 'translating') {
        // 从首页到翻译页
        // 1. 先fade-out其他元素
        fadeOut(fadeElements);
        
        setTimeout(() => {
          // 2. 语言选择器向上移动并缩小
          sourceSelector.style.transition = 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.3s ease-out';
          sourceSelector.style.transform = 'translateY(-200px) scale(0.9)';
          sourceSelector.style.opacity = '0.8';
          
          setTimeout(() => {
            // 3. 恢复透明度并fade-in新页面元素
            sourceSelector.style.opacity = '1';
            fadeIn(fadeElements);
            resolve();
          }, 600);
        }, 150);
        
      } else if (sourceSelector && fromPage === 'translating' && toPage === 'home') {
        // 从翻译页到首页
        // 1. 先fade-out其他元素
        fadeOut(fadeElements);
        
        setTimeout(() => {
          // 2. 语言选择器向下移动并放大
          sourceSelector.style.transition = 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.3s ease-out';
          sourceSelector.style.transform = 'translateY(200px) scale(1.1)';
          sourceSelector.style.opacity = '0.8';
          
          setTimeout(() => {
            // 3. 重置变换并fade-in新页面元素
            sourceSelector.style.transform = '';
            sourceSelector.style.opacity = '1';
            fadeIn(fadeElements);
            resolve();
          }, 600);
        }, 150);
        
      } else {
        // 没有动画元素时，执行简单的fade效果
        fadeOut(fadeElements);
        setTimeout(() => {
          fadeIn(fadeElements);
          resolve();
        }, 300);
      }
    });
  };

  // 开始翻译
  const handleStartTranslation = async () => {
    try {
      if (!selectedDeviceId) {
        console.error("No microphone device selected");
        return;
      }
      await startTranslation(selectedDeviceId);
    } catch (err) {
      console.error("Failed to start translation:", err);
    }
  };

  // 停止翻译
  const handleStopTranslation = async () => {
    try {
      await stopTranslation();
    } catch (err) {
      console.error("Failed to stop translation:", err);
    }
  };

  // 确定是否可以开始翻译
  const canStartTranslation = selectedDeviceId && 
                              isSessionServiceInitialized && 
                              hasPermission && 
                              !isTransitioning &&
                              sessionConnectionStatus !== SessionConnectionStatus.Connecting;

  // 确定是否正在加载
  const isLoading = sessionConnectionStatus === SessionConnectionStatus.Connecting || isTransitioning;

  // // 处理从聊天页面返回首页
  // const handleBackToHome = () => {
  //   handlePageTransition('home');
  // };

  // // 包装 setCurrentPage 以支持页面转换动画
  // const navigateToPage = (targetPage: PageType) => {
  //   if (onPageChange) {
  //     // 如果有外部回调，先执行转换动画再回调
  //     handlePageTransition(targetPage).then(() => {
  //       onPageChange(targetPage);
  //     });
  //   } else {
  //     // 否则使用内部状态管理
  //     handlePageTransition(targetPage);
  //   }
  // };

  return (
    <div ref={transitionRef} className={`relative overflow-hidden ${className}`}>
      {/* 页面内容 */}
      <div 
        className={`transition-opacity duration-300 ${
          isTransitioning ? 'opacity-50' : 'opacity-100'
        }`}
      >
        {currentPage === 'home' ? (
          <HomePage
            onStartTranslation={handleStartTranslation}
            isLoading={isLoading}
            disabled={!canStartTranslation}
          />
        ) : currentPage === 'translating' ? (
          <TranslatingPage
            // isSessionActive={isSessionActive}
            // sessionConnectionStatus={sessionConnectionStatus}
            // events={rtcEvents}
          />
        ) : (
          <ChatsPage />
        )}
      </div>

    </div>
  );
}; 